import Link from 'next/link';

import {
  BrandshopTrackPromotionEvent,
  createBrandshop,
} from '@/common/analytics/gtm/Homepage/brandshopsAnalytics';
import { trackGA4SelectPromotion } from '@/common/analytics/gtm/shared/trackPromotionClick';
import { LISTING_FUNCTIONS } from '@/common/analytics/gtm/types';
import BrandTile from '@/common/components/BrandTile/BrandTile';
import { FollowButton } from '@/common/components/FollowButton/FollowButton';
import SliderWithImpressions from '@/common/components/Slider/SliderWithImpressions';
import { medium, small, xLarge, xLarge2, xLarge3 } from '@/common/constants/media';
import { UseSlideImpression } from '@/common/impressions/useSliderImpression';
import { useCurrentPage } from '@/common/routes/ElysiumKnownPages';
import { Button } from '@/common/styleguide/atoms/Button/Button';
import { compileArrayToPairs } from '@/common/utils/sharedUtils';
import { RouteToBrowseBrandsPage } from '@/modules/BrowseBrands/BrowseBrandsConfig';
import { HomepageBrandsFeedItem } from 'generatedBfw/static';

import styles from './brandshops.module.scss';

const BrandFeedSliderItem = ({
  item,
  columnIndex,
  useSlideImpression,
}: {
  item: [HomepageBrandsFeedItem, HomepageBrandsFeedItem?];
  columnIndex: number;
  useSlideImpression: UseSlideImpression<BrandshopTrackPromotionEvent>;
}) => {
  const pageConfig = useCurrentPage();
  const pageType = pageConfig.analyticsGaName;
  return (
    <>
      {item.map((brand, rowIndex) => {
        if (!brand) return null;
        const metadata = createBrandshop(brand, columnIndex * 2 + rowIndex + 1);

        return (
          <BrandTile
            brand={{ ...brand, coverCoreUrl: `${brand.coverCoreUrl}.jpg` }}
            className={styles.brandSlide}
            classNameLink={styles.brandLink}
            footerSlot={
              <FollowButton
                authorId={brand.userId}
                className={styles.followButton}
                isFullWidth
                metadata={{
                  pageType,
                  componentLocation: 'brands_slider',
                  listingFunction: LISTING_FUNCTIONS.FOLLOW,
                  authorName: brand.name,
                }}
                onClick={() => trackGA4SelectPromotion(metadata)}
                size="small"
                variant="text"
              />
            }
            key={brand.slug}
            metadata={metadata}
            useBrandImpression={useSlideImpression}
          />
        );
      })}
    </>
  );
};

const BrandshopsView = ({ brands }: { brands: HomepageBrandsFeedItem[] }) => {
  const compiledBrandsFeed = compileArrayToPairs(brands);

  return (
    <section className={styles.container} data-testid="brandshops">
      <h2 className={styles.title}>
        Licensed artworks <br /> from top brands
      </h2>

      <Button
        as={Link}
        className={styles.link}
        data-testid="brandshops--link"
        href={RouteToBrowseBrandsPage().route}
        variant="text"
      >
        See all 200+ brands
      </Button>

      <div className={styles.brandsFeed} data-testid="brandshops--feed">
        <SliderWithImpressions<
          [HomepageBrandsFeedItem, HomepageBrandsFeedItem],
          BrandshopTrackPromotionEvent
        >
          keyExtractor={(item) => item[0].name}
          metadata={{ elementVariant: 'brandshops' }}
          options={{
            slidesPerGroupAuto: false,
            slidesPerView: 2.3,
            slidesPerGroup: 2,
            breakpoints: {
              [small]: {
                slidesPerView: 3.5,
                slidesPerGroup: 3,
              },
              [medium]: {
                slidesPerView: 2.4,
                slidesPerGroup: 2,
              },
              [xLarge]: {
                slidesPerView: 3,
                slidesPerGroup: 3,
              },
              [xLarge2]: {
                slidesPerView: 3.4,
                slidesPerGroup: 3,
              },
              [xLarge3]: {
                slidesPerView: 4.15,
                slidesPerGroup: 4,
              },
            },
          }}
          renderSlide={(item, index, useSlideImpression) => (
            <BrandFeedSliderItem
              columnIndex={index}
              item={item}
              useSlideImpression={useSlideImpression}
            />
          )}
          slideClassName={styles.slide}
          slides={compiledBrandsFeed}
        >
          <span className={styles.wrapperStart} slot="wrapper-start" />
          <span className={styles.wrapperEnd} slot="wrapper-end" />
        </SliderWithImpressions>
      </div>
    </section>
  );
};

export default BrandshopsView;
